<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { KkModal, KkButton } from '../../components/base'
import { displayHour } from '../../modules/regularHour'
import moment from 'moment'

const { t } = useI18n()


interface Period {
  openTime: string
  closeTime: string
}
interface DateTime {
  date: string
  periods: Period[]
}
interface DateTimesByYear {
  year: string
  dateTimes: DateTime[]
}
interface Props {
  isVisible: boolean
  dateTimes: DateTime[]
}
const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  dateTimes: () => []
})

interface Emits {
  (e: 'close'): void
}
const emit = defineEmits<Emits>()

const dateTimesDividedByYear = computed<DateTimesByYear[]>(() => {
  let dateTimesByYear: DateTimesByYear[] = []
  const years: Set<string> = new Set(props.dateTimes.map(dateTime => moment(dateTime.date).format('YYYY')))

  const dividedYearsHash: { [key: string]: DateTime[] } = {}
  years.forEach(((year: string) => dividedYearsHash[year] = []))

  props.dateTimes.forEach(dateTime => dividedYearsHash[moment(dateTime.date).format('YYYY')].push(dateTime))

  Object.keys(dividedYearsHash).forEach((key: string) => dateTimesByYear.push({ year: key, dateTimes: dividedYearsHash[key] }))

  return dateTimesByYear
})

</script>
<template>
  <KkModal width="720px" :is-visible="props.isVisible" @change="emit('close')">
    <div class="content__header">
      {{ t('pages.CompaniesLocationStore.businessHours.pastSpecialHoursModal.title') }}
    </div>
    <div class="content__body">
      <div v-for="(dateTimesByYear, i) in dateTimesDividedByYear" :key="i" class="content__yearSection">
        <div class="p-store-times__title">
          {{ dateTimesByYear.year }}年
        </div>
        <ul v-for="(dateTime, n) in dateTimesByYear.dateTimes" :key="n" class="p-store-times__items">
          <li class="p-store-times__item">
            <div class="p-store-times__title">
              {{ dateTime.date }}
            </div>
            <span class="p-store-times__content">
              {{ displayHour('specialHour')(dateTime) }}
            </span>
          </li>
        </ul>
      </div>
    </div>
    <div class="content__footer">
      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton
            color="secondary"
            outlined
            :text="t('labels.close')"
            @click="emit('close')"
          />
        </div>
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
.content {
  &__header {
    padding: 24px 32px 16px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-bottom: 1px solid #e4e7eb;
    font-weight: 700;
    font-size: 20px;
    line-height: 1.5;
  }

  &__body {
    padding: 24px;
    height: 50vh;
    overflow-y: scroll;
  }

  &__yearSection {
    margin-bottom: 40px;
  }

  &__item {
    padding: 0 12px 12px;
  }

  &__footer {
    padding: 32px;
    border-top: 1px solid #e4e7eb;
  }
}

.buttonGroup {
  display: flex;
  flex-grow: 0;
  justify-content: flex-end;
}

.p-store-times {
  &__items {
    &:not(:first-child) {
      margin-top: 14px;
    }
  }
  &__item {
    display: flex;
    &:not(:first-child) {
      border-top: 1px solid #E4E7EB;
    }
  }
  &__title {
    font-weight: bold;
  }
  &__content {
    margin-left: 20px;
  }
}
</style>
